import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as assistantApi from '../api/assistant';
import { useAssistantPageContextState } from '../context';
import { useAppSelector } from '@store/store';
import {
  selectCurrentDomain,
  selectSiteSettings,
} from '@store/slices/settingsSlice';
import { selectProfile } from '@store/slices/userSlice';
import type {
  AssistantMessageMetadata,
  ChatMessage,
  MessageRequest,
  AssistantFeedback,
  AssistantActionResolution,
} from '@app-types/assistant.ts';

const ASSISTANT_QUERY_ROOT = ['assistant'] as const;
const ASSISTANT_CHANNEL = 'df-av-assistant-state-v2';

export interface UseAssistantChatReturn {
  sessionId: number | null;
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  conversationState: 'idle' | 'active' | 'ended' | 'expired';
  conversationRevision: number | null;
  maxMessageCharacters: number;
  initSession: () => Promise<void>;
  sendMessage: (
    message: string,
    useRag?: boolean,
    metadata?: AssistantMessageMetadata
  ) => Promise<void>;
  loadHistory: () => Promise<void>;
  clearSession: () => void;
  endConversation: () => Promise<void>;
  endAndStartNew: () => Promise<void>;
  submitFeedback: (
    messageId: number,
    feedback: AssistantFeedback
  ) => Promise<void>;
  resolveAction: (
    messageId: number,
    actionId: string
  ) => Promise<AssistantActionResolution>;
  hasSession: boolean;
  isEmpty: boolean;
}

const getErrorMessage = (error: unknown): string => {
  const data = (
    error as {
      response?: { data?: { error?: { message?: string }; detail?: string } };
    }
  )?.response?.data;
  return (
    data?.error?.message || data?.detail || 'No se pudo completar la operación.'
  );
};

export const countAssistantCharacters = (value: string): number =>
  Array.from(value).length;

export const shouldApplyAssistantResponse = ({
  expectedGeneration,
  currentGeneration,
  expectedSession,
  currentSession,
  expectedRequest,
  activeRequest,
  responseSession,
  responseRequest,
}: {
  expectedGeneration: number;
  currentGeneration: number;
  expectedSession: number;
  currentSession: number | null;
  expectedRequest: string;
  activeRequest: string | null;
  responseSession: number;
  responseRequest: string;
}): boolean =>
  expectedGeneration === currentGeneration &&
  expectedSession === currentSession &&
  expectedRequest === activeRequest &&
  responseRequest === expectedRequest &&
  responseSession === expectedSession;

export const useAssistantChat = (): UseAssistantChatReturn => {
  const queryClient = useQueryClient();
  const profile = useAppSelector(selectProfile);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const site = useAppSelector(selectSiteSettings);
  const currentDomain = useAppSelector(selectCurrentDomain);
  const { effectiveContext } = useAssistantPageContextState();
  const siteScope = site?.site_code || currentDomain || window.location.host;
  const actorScope =
    isAuthenticated && profile?.id ? `user:${profile.id}` : 'anonymous';
  const scopeKey = `${siteScope}|${actorScope}`;

  const [sessionId, setSessionId] = useState<number | null>(null);
  const [conversationRevision, setConversationRevision] = useState<
    number | null
  >(null);
  const [conversationState, setConversationState] =
    useState<UseAssistantChatReturn['conversationState']>('idle');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const generationRef = useRef(0);
  const scopeRef = useRef(scopeKey);
  const sessionRef = useRef<number | null>(null);
  const activeRequestRef = useRef<string | null>(null);
  const scopeChanged = scopeRef.current !== scopeKey;

  useEffect(() => {
    sessionRef.current = sessionId;
  }, [sessionId]);

  const purgeLocalConversation = useCallback(() => {
    generationRef.current += 1;
    activeRequestRef.current = null;
    sessionRef.current = null;
    setSessionId(null);
    setConversationRevision(null);
    setConversationState('idle');
    setMessages([]);
    setError(null);
    void queryClient.cancelQueries({ queryKey: ASSISTANT_QUERY_ROOT });
    queryClient.removeQueries({ queryKey: ASSISTANT_QUERY_ROOT });
  }, [queryClient]);

  useEffect(() => {
    if (scopeRef.current === scopeKey) return;
    scopeRef.current = scopeKey;
    purgeLocalConversation();
  }, [purgeLocalConversation, scopeKey]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(ASSISTANT_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data?.scope === scopeRef.current) purgeLocalConversation();
    };
    return () => channel.close();
  }, [purgeLocalConversation]);

  const announceInvalidation = useCallback(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(ASSISTANT_CHANNEL);
    channel.postMessage({ type: 'invalidate', scope: scopeRef.current });
    channel.close();
  }, []);

  const initSessionMutation = useMutation({
    retry: false,
    mutationFn: assistantApi.startSession,
  });
  const initSession = useCallback(async () => {
    const generation = generationRef.current;
    try {
      const result = await initSessionMutation.mutateAsync({
        version: 2,
        mode: 'resume_or_create',
      });
      if (generation !== generationRef.current) return;
      sessionRef.current = result.session_id;
      setSessionId(result.session_id);
      setConversationRevision(result.conversation_revision);
      setConversationState(result.state);
      setError(null);
    } catch (err) {
      if (generation === generationRef.current) setError(getErrorMessage(err));
      throw err;
    }
  }, [initSessionMutation]);

  const historyKey = useMemo(
    () => [
      'assistant',
      'history',
      siteScope,
      actorScope,
      sessionId,
      conversationRevision,
    ],
    [actorScope, conversationRevision, sessionId, siteScope]
  );
  const historyQuery = useQuery({
    queryKey: historyKey,
    queryFn: () => assistantApi.getHistory(sessionId!, 50, 0),
    enabled: sessionId !== null && conversationState === 'active',
    placeholderData: undefined,
    staleTime: 30_000,
    retry: false,
  });

  useEffect(() => {
    const history = historyQuery.data;
    if (!history || history.session_id !== sessionRef.current) return;
    setMessages(history.messages);
    setConversationRevision(history.conversation_revision);
    setConversationState(history.state);
  }, [historyQuery.data]);

  const sendMessageMutation = useMutation({
    retry: false,
    mutationFn: ({
      targetSession,
      request,
    }: {
      targetSession: number;
      request: MessageRequest;
    }) => assistantApi.sendMessage(targetSession, request),
  });

  const sendMessage = useCallback(
    async (
      rawMessage: string,
      _useRag = true,
      metadata?: AssistantMessageMetadata
    ) => {
      const message = rawMessage.trim();
      if (!message || countAssistantCharacters(message) > 4000) {
        setError('El mensaje debe tener entre 1 y 4.000 caracteres.');
        throw new Error('invalid_message_length');
      }
      const targetSession = sessionRef.current;
      if (targetSession === null || conversationState !== 'active') {
        setError('La conversación no está activa.');
        throw new Error('conversation_not_active');
      }
      if (activeRequestRef.current) return;

      const requestId = crypto.randomUUID();
      const generation = generationRef.current;
      activeRequestRef.current = requestId;
      const request: MessageRequest = {
        version: 2,
        request_id: requestId,
        message,
        page_context: effectiveContext,
        metadata,
      };
      try {
        const result = await sendMessageMutation.mutateAsync({
          targetSession,
          request,
        });
        if (
          !shouldApplyAssistantResponse({
            expectedGeneration: generation,
            currentGeneration: generationRef.current,
            expectedSession: targetSession,
            currentSession: sessionRef.current,
            expectedRequest: requestId,
            activeRequest: activeRequestRef.current,
            responseSession: result.session_id,
            responseRequest: result.request_id,
          })
        )
          return;
        const assistantMessage = result.assistant_message
          ? {
              ...result.assistant_message,
              status: result.status,
              questions: result.questions,
              actions: result.actions,
            }
          : null;
        const next = [result.user_message, assistantMessage].filter(
          Boolean
        ) as ChatMessage[];
        setMessages((current) => [...current, ...next]);
        setConversationRevision(result.conversation_revision);
        setError(result.notice || null);
      } catch (err) {
        if (
          generation === generationRef.current &&
          targetSession === sessionRef.current
        ) {
          setError(getErrorMessage(err));
        }
        throw err;
      } finally {
        if (activeRequestRef.current === requestId)
          activeRequestRef.current = null;
      }
    },
    [conversationState, effectiveContext, sendMessageMutation]
  );

  const loadHistory = useCallback(async () => {
    await historyQuery.refetch();
  }, [historyQuery]);

  const clearSession = useCallback(() => {
    purgeLocalConversation();
    announceInvalidation();
  }, [announceInvalidation, purgeLocalConversation]);

  const endConversation = useCallback(async () => {
    const targetSession = sessionRef.current;
    if (targetSession === null) return;
    const generation = generationRef.current;
    try {
      const result = await assistantApi.endSession(targetSession);
      if (
        generation !== generationRef.current ||
        targetSession !== sessionRef.current
      )
        return;
      setConversationRevision(result.conversation_revision);
      setConversationState(result.state);
      announceInvalidation();
    } catch (err) {
      if (generation === generationRef.current) setError(getErrorMessage(err));
      throw err;
    }
  }, [announceInvalidation]);

  const endAndStartNew = useCallback(async () => {
    await endConversation();
    purgeLocalConversation();
    await initSession();
  }, [endConversation, initSession, purgeLocalConversation]);

  const feedbackMutation = useMutation({
    retry: false,
    mutationFn: ({
      targetSession,
      messageId,
      feedback,
    }: {
      targetSession: number;
      messageId: number;
      feedback: AssistantFeedback;
    }) =>
      assistantApi.submitFeedback(targetSession, messageId, {
        version: 2,
        ...feedback,
      }),
  });

  const submitFeedback = useCallback(
    async (messageId: number, feedback: AssistantFeedback) => {
      const targetSession = sessionRef.current;
      if (targetSession === null) throw new Error('conversation_not_available');
      const generation = generationRef.current;
      try {
        const result = await feedbackMutation.mutateAsync({
          targetSession,
          messageId,
          feedback,
        });
        if (
          generation !== generationRef.current ||
          targetSession !== sessionRef.current ||
          result.message_id !== messageId
        )
          return;
        setMessages((current) =>
          current.map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  feedback: {
                    rating: result.rating,
                    reason_code: result.reason_code,
                  },
                }
              : message
          )
        );
        setError(null);
      } catch (err) {
        if (generation === generationRef.current)
          setError(getErrorMessage(err));
        throw err;
      }
    },
    [feedbackMutation]
  );

  const actionMutation = useMutation({
    retry: false,
    mutationFn: ({
      targetSession,
      messageId,
      actionId,
      revision,
    }: {
      targetSession: number;
      messageId: number;
      actionId: string;
      revision: number;
    }) =>
      assistantApi.resolveAction(targetSession, messageId, actionId, revision),
  });

  const resolveAction = useCallback(
    async (messageId: number, actionId: string) => {
      const targetSession = sessionRef.current;
      if (targetSession === null || conversationRevision === null)
        throw new Error('conversation_not_available');
      try {
        const result = await actionMutation.mutateAsync({
          targetSession,
          messageId,
          actionId,
          revision: conversationRevision,
        });
        if (targetSession !== sessionRef.current)
          throw new Error('assistant_scope_changed');
        setError(null);
        return result;
      } catch (err) {
        setError(getErrorMessage(err));
        throw err;
      }
    },
    [actionMutation, conversationRevision]
  );

  return {
    sessionId: scopeChanged ? null : sessionId,
    messages: scopeChanged ? [] : messages,
    loading:
      initSessionMutation.isPending ||
      sendMessageMutation.isPending ||
      historyQuery.isLoading,
    error,
    conversationState,
    conversationRevision,
    maxMessageCharacters: 4000,
    initSession,
    sendMessage,
    loadHistory,
    clearSession,
    endConversation,
    endAndStartNew,
    submitFeedback,
    resolveAction,
    hasSession:
      !scopeChanged && sessionId !== null && conversationState === 'active',
    isEmpty: scopeChanged || messages.length === 0,
  };
};
